import { Injectable, inject } from '@angular/core';

import { Observable, map, of } from 'rxjs';

import { mapAuctionActivityResponse, mapAuctionResultResponse } from '@core/models/auction-result.mapper';
import { AuctionActivityResponse, AuctionResultResponse } from '@core/models/auction-result.response';
import { AuctionActivityState, AuctionResultState } from '@core/models/auction-result.state';
import { mapAuctionResponse, mapBidOutcomeResponse, mapBidResponse } from '@core/models/auction.mapper';
import { AuctionResponse, BidOutcomeResponse, BidResponse } from '@core/models/auction.response';
import { AuctionState, BidOutcomeState, BidState } from '@core/models/auction.state';
import { FulfilmentMethod } from '@core/models/listing.enum';
import { ApiService } from '@core/services/api.service';

import { AuctionRepository } from './auction.repository';

@Injectable()
export class AuctionHttpRepository implements AuctionRepository {
  private readonly api = inject(ApiService);

  getByListingId(listingId: number): Observable<AuctionState | null> {
    return this.api
      .get<AuctionResponse | null>(`listings/${listingId}/auction`)
      .pipe(map(response => (response ? mapAuctionResponse(response) : null)));
  }

  listBids(auctionId: number): Observable<BidState[]> {
    return this.api.get<BidResponse[]>(`auctions/${auctionId}/bids`).pipe(map(bids => bids.map(mapBidResponse)));
  }

  placeBid(
    auctionId: number,
    shopperId: number,
    maxBid: number,
    fulfilment?: FulfilmentMethod
  ): Observable<BidOutcomeState> {
    return this.api
      .post<BidOutcomeResponse>(`auctions/${auctionId}/bids`, { shopperId, maxBid, fulfilment: fulfilment ?? null })
      .pipe(map(mapBidOutcomeResponse));
  }

  getResult(auctionId: number): Observable<AuctionResultState | null> {
    return this.api
      .get<AuctionResultResponse | null>(`auctions/${auctionId}/result`)
      .pipe(map(response => (response ? mapAuctionResultResponse(response) : null)));
  }

  listActivity(shopperId: number): Observable<AuctionActivityState[]> {
    return this.api
      .get<AuctionActivityResponse[]>(`shoppers/${shopperId}/auction-activity`)
      .pipe(map(items => items.map(mapAuctionActivityResponse)));
  }

  sweepClosed(): Observable<number> {
    // No-op over HTTP: the auction close engine runs server-side (HLD §9.3).
    return of(0);
  }
}
